import { FC } from 'react';
import { useSelector } from 'react-redux';
import moment, { Moment } from 'moment';
import { RootState } from 'store';
import { parseDateToUSFormat } from 'utils/string.utils';
import { profileClockReportSlice } from '../../store/profileReports';
import ReportTable, { ReportColumn } from './ReportTable';
import { ProfileDateRangeProps } from './useProfileDateRange';

const CLOCK_RANGES: Record<string, [Moment, Moment]> = {
  'This month': [moment().startOf('month'), moment().endOf('month')],
  'Last week': [moment().subtract(1, 'week').startOf('week'), moment().subtract(1, 'week').endOf('week')],
  'This week': [moment().startOf('week'), moment().endOf('week')],
};

interface ClockReportProps extends ProfileDateRangeProps {
  userUuid: string;
}

const formatClockTime = (iso?: string) => {
  if (!iso) return '-';

  const match = /T(\d{2}):(\d{2})/.exec(iso);

  return match ? moment(`${match[1]}:${match[2]}`, 'HH:mm').format('h:mm a') : '-';
};

const formatAvgTime = (value?: string | null) => (value ? moment(value, 'HH:mm').format('h:mm a') : null);

const formatDailyTime = (value: unknown) => {
  if (typeof value !== 'number') return '-';

  const hours = Math.floor(value / 60);
  const minutes = Math.round(value % 60);

  return `${hours}:${String(minutes).padStart(2, '0')}`;
};

const columns: ReportColumn[] = [
  { title: 'Date', dataIndex: 'date', width: '40%', render: (value) => (value ? parseDateToUSFormat(value) : '-') },
  { title: 'Start time', dataIndex: 'clock_in', width: '25%', subtitle: 'First time start', render: formatClockTime },
  { title: 'End time', dataIndex: 'clock_out', width: '25%', subtitle: 'Last time log entered', render: formatClockTime },
  { title: 'Time span', dataIndex: 'span', width: '10%', render: (value) => formatDailyTime(value) },
];

const ClockReport: FC<ClockReportProps> = ({ userUuid, dateRange, onDateRangeChange }) => {
  const totals = useSelector((store: RootState) => store.profileClockReport.clockTotals);

  return (
    <ReportTable
      storeSlice={profileClockReportSlice}
      columns={columns}
      userUuid={userUuid}
      dateRange={dateRange}
      onDateRangeChange={onDateRangeChange}
      userFilterKey="user"
      ranges={CLOCK_RANGES}
      countLabel="entries"
      averages={[
        { label: 'Avg start', value: formatAvgTime(totals?.avgClockIn) },
        { label: 'Avg end', value: formatAvgTime(totals?.avgClockOut) },
      ]}
    />
  );
};

export default ClockReport;
